import { formatCurrency } from '@dcp/shared/utils/formatters'
import type { CashPositionSummary } from '@/hooks/cashPositionSummaries'
import type { CashPositionPeriod } from '@/lib/date-utils'
import type { CashPositionWarningKey } from './cashPositionWarnings'

/**
 * Section colour tone tagged onto every row so the grid can tint each block's
 * heading band (and a subtle label-side accent) without re-deriving membership.
 * Restrained, low-saturation hues chosen for professional separability:
 *
 *  - `indigo`  — the balances block (Bank Balances … Book vs Intacct Variance).
 *    NOTE: this block no longer carries a "Balances" heading row (DCP-143) — the
 *    indigo left-accent alone ties the rows together as a group.
 *  - `blue`    — the LINE OF CREDIT block (DCP-147): the LOC borrowing base,
 *    usage, remaining availability and the CASH (Book) + CREDIT AVAILABLE
 *    punchline. Period-independent (rendered once) like the balances block. Its
 *    heading is the emphasized one — a larger, heavier blue band matching the BA
 *    mockup (blue-100 band / blue-800 text / blue-600 rule, 15px).
 *  - `amber`   — the per-period projection block ("Remainder of Week" / "N Weeks
 *    Out").
 */
export type SectionTone = 'indigo' | 'blue' | 'amber'

/**
 * Discriminated row descriptor driving the Cash Position Summary presentation
 * (DCP-143 / DCP-144).
 *
 *  - `section`   — a block heading row (e.g. "Remainder of Week (4 days)"); no
 *                  value cells.
 *  - `value`     — a normal line item with a label.
 *  - `total`     — an emphasized subtotal/total line (bold + subtle background).
 *
 * Every row carries `tone`, the colour of the block it belongs to, so the grid
 * can paint the heading band and a faint label accent per section.
 *
 * `emphasis: 'caps'` upper-cases the label and gives it the strongest weight
 * (used for BOOK BALANCE / the per-period ending Book Balance row).
 *
 * The two presentation flags below are set by the builders (NOT inferred from the
 * label), so the grid never string-matches labels — critical now that the ending
 * row's label varies per period (DCP-144: "Expected EOW Book Balance" for the
 * remainder vs "Ending Book Balance" for full weeks):
 *  - `semanticEow`      — the block's bottom-line ending balance; its numbers get
 *                         semantic sign colour (emerald good / red bad) and the
 *                         heaviest bottom rule.
 *  - `projectedSubtotal`— the "Projected Inflows" / "Projected Outflows" rows,
 *                         which read as emphasized amber subtotals.
 *
 * `field` is a pure selector mapping a {@link CashPositionSummary} to the row's
 * value. Keeping a selector (instead of a baked-in number) lets the multi-column
 * grid reuse one row list and pull the value per facility column. It returns
 * `number | null`: most rows are always numeric, but the LOC block (DCP-147) can
 * yield `null` (no borrowing base → rendered as an em-dash), so the grid must
 * handle null per cell.
 */
/**
 * Row descriptor WITHOUT its section tone. Blocks are declared in this shape so
 * a block's tone can be stamped on uniformly in one place, rather than repeated
 * on every row. Kept as an explicit union (not `Omit<SummaryRow, 'tone'>`)
 * because `Omit` over a discriminated union drops the per-variant `field`.
 */
type UntonedRow =
  | { kind: 'section'; label: string }
  | {
      kind: 'value'
      label: string
      field: (s: CashPositionSummary) => number | null
      /**
       * Marks a value row whose per-column cells expose a "View" drill-down
       * affordance (DCP-169) — currently only "LOC Usage", which opens the real
       * LINE OF CREDIT transactions behind that column's usage. Flag-driven, NOT
       * label-matched: the grid keys the affordance off this flag so behaviour
       * never string-matches the label (repo/Presto convention).
       */
      drillable?: true
      /**
       * Names WHICH missing-data signal this row surfaces per cell (DCP-161), e.g.
       * the Bank Balances row shows bank-coverage warnings and the Intacct Balance
       * row shows Intacct sync warnings. The grid looks the key up in the warning
       * map it is given; when the map has no entry the cell renders unchanged.
       *
       * Flag-driven like `drillable` above — the grid must NEVER decide this by
       * matching a label, because block labels vary per period and per ticket.
       */
      warningKey?: CashPositionWarningKey
    }
  | {
      kind: 'total'
      label: string
      field: (s: CashPositionSummary) => number | null
      emphasis?: 'caps'
      /** Marks the block's bottom-line ending balance (semantic sign colour). */
      semanticEow?: true
      /** Marks the "Projected Inflows"/"Projected Outflows" emphasized subtotals. */
      projectedSubtotal?: true
    }

export type SummaryRow = UntonedRow & { tone: SectionTone }

/**
 * Stamps a tone-less block with its section tone, turning each `UntonedRow` into
 * a full `SummaryRow` (= UntonedRow & { tone }) — no cast needed. Declaring tone
 * once per block keeps section→tone membership from drifting row-by-row.
 */
const withTone = (rows: UntonedRow[], tone: SectionTone): SummaryRow[] =>
  rows.map((r) => ({ ...r, tone }))

/**
 * Builds the indigo balances block (Bank Balances … Book vs Intacct Variance).
 *
 * These figures are period-independent (a facility has one current bank/book/
 * intacct balance), so the grid renders this block ONCE — reading each column's
 * period-0 summary — regardless of how many forecast periods are shown (DCP-144).
 * No "Balances" section heading (DCP-143): the block opens directly with "Bank
 * Balances" and the shared indigo left-accent ties the rows together.
 *
 * @returns the balances rows in display order
 */
export function buildBalancesRows(): SummaryRow[] {
  const balances: UntonedRow[] = [
    // The two rows carrying missing-data warnings (DCP-161). Both are sourced from
    // data a user can be late on — a manually entered bank balance and a manually
    // triggered Intacct import — unlike Outstanding Checks (Sage-owned) or the two
    // derived totals, which are left clean per the BA prototype.
    { kind: 'value', label: 'Bank Balances', field: (s) => s.bankBalance, warningKey: 'bankBalance' },
    { kind: 'value', label: 'Outstanding Checks', field: (s) => s.outstandingChecks },
    { kind: 'total', label: 'Book Balance', field: (s) => s.bookBalance, emphasis: 'caps' },
    {
      kind: 'value',
      label: 'Intacct Balance',
      field: (s) => s.intacctBalance,
      warningKey: 'intacctBalance',
    },
    { kind: 'total', label: 'Book vs Intacct Variance', field: (s) => s.bookVsIntacct },
  ]
  return withTone(balances, 'indigo')
}

/**
 * Builds the blue LINE OF CREDIT block (DCP-147): the LOC section heading, the
 * borrowing base, the usage, the remaining credit availability, and the CASH
 * (Book) + CREDIT AVAILABLE punchline.
 *
 * Like the balances block these figures are period-independent (a facility's LOC
 * base/usage is a current snapshot, not a forecast), so the grid renders this
 * block ONCE — reading each column's period-0 summary — and places it AFTER the
 * balances block and BEFORE the first projection period. The whole block is
 * blue-toned (its heading is the emphasized, larger band — see the mockup).
 *
 * Business rule (verbatim from the BA prototype): when `locBorrowingBase` is null
 * — a pack-member column, or a facility with no standalone LOC line — BOTH
 * "Remaining Credit Availability" AND "CASH (Book) + CREDIT AVAILABLE" return
 * null (→ rendered as an em-dash), NOT a book+0 fallback. Such columns show only
 * their own LOC usage; the base/remaining/cash+credit live at the pack line, not
 * the member. Only "LOC Usage" is always a number.
 *
 * @returns the LOC rows in display order
 */
export function buildLocRows(): SummaryRow[] {
  const loc: UntonedRow[] = [
    { kind: 'section', label: 'LINE OF CREDIT' },
    { kind: 'total', label: 'LOC Borrowing Base', field: (s) => s.locBorrowingBase },
    // Drillable (DCP-169): each facility/pack column's LOC Usage cell exposes a
    // "View" affordance opening the real LINE OF CREDIT transactions behind it.
    { kind: 'value', label: 'LOC Usage', field: (s) => s.locUsage, drillable: true },
    {
      kind: 'total',
      label: 'Remaining Credit Availability',
      // Null base → null (dash), never base-less arithmetic.
      field: (s) => (s.locBorrowingBase == null ? null : s.locBorrowingBase - s.locUsage),
    },
    {
      kind: 'total',
      label: 'CASH (Book) + CREDIT AVAILABLE',
      emphasis: 'caps',
      // Null base → null (dash); otherwise book balance + remaining availability.
      field: (s) => (s.locBorrowingBase == null ? null : s.bookBalance + (s.locBorrowingBase - s.locUsage)),
    },
  ]
  return withTone(loc, 'blue')
}

/**
 * Builds the amber projection block for ONE forecast period (DCP-144): a section
 * heading, the projected inflows/outflows detail, the inter-co/LOC lines, the net
 * projected cash flow, and the block's ending balance.
 *
 * The heading and the ending-row label vary by period:
 *  - Remainder period → heading "Remainder of Week (N days)" (business-day
 *    count, Mon–Fri only, per the DCP-143 spec — decoupled from the query
 *    window, which spans today through this week's Saturday inclusive with
 *    no business-day filtering) and ending row "Expected EOW Book Balance".
 *  - Full-week period → heading "<label> (<M/D/YYYY - M/D/YYYY>)" and ending row
 *    "Ending Book Balance".
 *
 * The ending row is flagged `semanticEow` (not detected by label) so the grid can
 * apply the semantic sign colour + heaviest rule regardless of its varying label.
 *
 * @param period - the period descriptor whose window this block renders
 * @returns the projection rows in display order for that period
 */
export function buildProjectionRows(period: CashPositionPeriod): SummaryRow[] {
  // Heading: business-day count (Mon–Fri, decoupled from the widened query
  // window) for the remainder, calendar range for full weeks.
  const headingLabel =
    period.key === 'remainder'
      ? `Remainder of Week (${period.businessDays ?? 0} days)`
      : `${period.label} (${period.dateRangeLabel})`

  // Ending-row label differs per period; the semanticEow flag (not this string)
  // is what the grid keys its styling off.
  const endingLabel = period.key === 'remainder' ? 'Expected EOW Book Balance' : 'Ending Book Balance'

  const projection: UntonedRow[] = [
    { kind: 'section', label: headingLabel },
    { kind: 'total', label: 'Projected Inflows', field: (s) => s.projectedInflows, projectedSubtotal: true },
    { kind: 'value', label: 'Accts Payable', field: (s) => s.outflows.acctsPayable },
    { kind: 'value', label: 'Payroll', field: (s) => s.outflows.payroll },
    { kind: 'value', label: 'Credit Card', field: (s) => s.outflows.creditCard },
    { kind: 'value', label: 'Mgmt Fee', field: (s) => s.outflows.mgmtFee },
    { kind: 'value', label: 'Distribution', field: (s) => s.outflows.distribution },
    { kind: 'value', label: 'Rent', field: (s) => s.outflows.rent },
    { kind: 'value', label: 'Escrow', field: (s) => s.outflows.escrow },
    { kind: 'value', label: 'Cap Ex', field: (s) => s.outflows.capEx },
    { kind: 'value', label: 'Chargebacks', field: (s) => s.outflows.chargebacks },
    { kind: 'value', label: 'Minimum Daily Bank Balance', field: (s) => s.outflows.minimumDailyBankBalance },
    { kind: 'total', label: 'Projected Outflows', field: (s) => s.projectedOutflows, projectedSubtotal: true },
    { kind: 'value', label: 'Inter Co Loan', field: (s) => s.interCoLoan },
    { kind: 'value', label: 'Line of Credit', field: (s) => s.lineOfCredit },
    { kind: 'total', label: 'Inter-Co Loan/LOC Projections', field: (s) => s.interCoLoanLocProjections },
    { kind: 'total', label: 'Net Projected Cash Flow', field: (s) => s.netProjectedCashFlow },
    { kind: 'total', label: endingLabel, field: (s) => s.expectedEowBookBalance, emphasis: 'caps', semanticEow: true },
  ]

  return withTone(projection, 'amber')
}

/**
 * Formats a signed money value for the summary, whole-dollar with negatives in
 * accounting parentheses (e.g. `($1,025)`) to match the mockup. Routed through
 * the shared `formatCurrency` so locale/currency stay centralized.
 *
 * Zeros are intentionally NOT collapsed to a dash — the grid shows every value
 * (0 → "$0") and de-emphasizes zeros with a muted text colour instead, so real
 * figures stand out while the reader can still confirm a cell is truly zero.
 *
 * Module-local: every caller goes through {@link formatCell} (the nullable
 * wrapper), which is the module's single formatting export.
 *
 * @param value - the signed amount (outflows are negative)
 * @returns the formatted currency string
 */
function formatMoney(value: number): string {
  return formatCurrency(value, { decimals: 0, negativeStyle: 'parentheses' })
}

/**
 * Formats a nullable cell value for the grid: `null` renders as an em-dash (a
 * LOC row with no borrowing base — see {@link buildLocRows}), any number goes
 * through {@link formatMoney}. Centralized here so the Combined Total cell and
 * the per-column cells format identically (DCP-147).
 *
 * @param value - the signed amount, or null for a no-data cell
 * @returns the formatted currency string, or "—" when null
 */
export function formatCell(value: number | null): string {
  return value == null ? '—' : formatMoney(value)
}
